import JSZip from "jszip";

import { describeCrop, normalizeCropCode } from "pages/admin/experiments/cropExtraction/cropCodes";
import { classifyZip } from "pages/admin/experiments/cropExtraction/formatDetection";
import { parseAndi } from "pages/admin/experiments/cropExtraction/parsers/andi";
import { parseDataExperts } from "pages/admin/experiments/cropExtraction/parsers/dataExperts";
import { parseDianaweb } from "pages/admin/experiments/cropExtraction/parsers/dianaweb";
import { parseElanNrw } from "pages/admin/experiments/cropExtraction/parsers/elanNrw";
import { parseElanRlp } from "pages/admin/experiments/cropExtraction/parsers/elanRlp";
import { parseFiona } from "pages/admin/experiments/cropExtraction/parsers/fiona";
import { ensureShpLoaded } from "pages/admin/experiments/cropExtraction/parsers/genericDbf";
import { parseHessenPolygone } from "pages/admin/experiments/cropExtraction/parsers/hessenPolygone";
import { parseIBalis } from "pages/admin/experiments/cropExtraction/parsers/iBalis";
import { parseSchleswigHolstein } from "pages/admin/experiments/cropExtraction/parsers/schleswigHolstein";
import {
  CropRecord,
  ERROR_TYPES,
  ExtractionResult,
  ExtractionSummary,
  FormatClassification,
  ParserResult,
  Vendor,
} from "pages/admin/experiments/cropExtraction/types";

const MAX_FILE_SIZE_BYTES = 50 * 1024 * 1024;
const PROCESSING_TIMEOUT_MS = 30_000;

const emptyClassification = (): FormatClassification => ({
  vendor: "unknown",
  formatFamily: "Unknown vendor",
  confidence: "low",
  detectionSignals: [],
  supported: false,
  regionHint: "Unknown",
});

const buildSummary = (crops: CropRecord[]): ExtractionSummary => {
  const distribution: Record<string, number> = {};
  let totalArea = 0;
  for (const c of crops) {
    if (c.cropCode) distribution[c.cropCode] = (distribution[c.cropCode] || 0) + 1;
    totalArea += c.areaHa;
  }
  let topCrop: ExtractionSummary["topCrop"] = null;
  for (const [code, count] of Object.entries(distribution)) {
    if (!topCrop || count > topCrop.count) {
      topCrop = { code, description: describeCrop(code) || "", count };
    }
  }
  return {
    totalFields: crops.length,
    totalArea: Math.round(totalArea * 100) / 100,
    avgFieldSize: crops.length > 0 ? Math.round((totalArea / crops.length) * 100) / 100 : 0,
    topCrop,
    cropDistribution: distribution,
  };
};

const dispatchVendor = async (vendor: Vendor, jszip: JSZip, zipBuffer: ArrayBuffer): Promise<ParserResult> => {
  switch (vendor) {
    case "data-experts":
      return parseDataExperts(jszip);
    case "andi":
      return parseAndi(jszip);
    case "ibalis":
      await ensureShpLoaded();
      return parseIBalis(zipBuffer);
    case "dianaweb":
      await ensureShpLoaded();
      return parseDianaweb(zipBuffer);
    case "hessen-polygone":
      await ensureShpLoaded();
      return parseHessenPolygone(zipBuffer);
    case "fiona":
      await ensureShpLoaded();
      return parseFiona(zipBuffer);
    case "elan-rlp":
      await ensureShpLoaded();
      return parseElanRlp(zipBuffer);
    case "elan-nrw":
      return parseElanNrw(jszip);
    case "elan-sh":
      await ensureShpLoaded();
      return parseSchleswigHolstein(zipBuffer);
    default:
      return { method: "unsupported", crops: [], warnings: [`No parser for vendor ${vendor}`] };
  }
};

const withTimeout = <T>(promise: Promise<T>, ms: number): Promise<T> =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error(ERROR_TYPES.TIMEOUT)), ms);
    promise.then(
      (v) => {
        clearTimeout(timer);
        resolve(v);
      },
      (e) => {
        clearTimeout(timer);
        reject(e);
      }
    );
  });

const enrichCrops = (crops: CropRecord[], vendor: Vendor): { crops: CropRecord[]; warnings: string[] } => {
  const warnings: string[] = [];
  const enriched = crops.map((c) => {
    const code = normalizeCropCode(c.cropCode);
    let description = c.cropDescription || describeCrop(code, vendor);
    if (!description && code) {
      warnings.push(`Unmapped crop code: ${code}`);
      description = "";
    }
    return { ...c, cropCode: code, cropDescription: description };
  });
  return { crops: enriched, warnings };
};

const dedupeWarnings = (warnings: string[]): string[] => Array.from(new Set(warnings));

// Widened to `Blob` so callers can pass a fetch() body directly. `File`
// extends `Blob`, so existing upload-based callers still type-check.
export const extractCropsFromZip = async (file: Blob): Promise<ExtractionResult> => {
  const start = Date.now();
  const errors: string[] = [];
  const warnings: string[] = [];

  if (file.size > MAX_FILE_SIZE_BYTES) {
    return {
      success: false,
      classification: emptyClassification(),
      inventory: [],
      extraction: null,
      summary: null,
      errors: [ERROR_TYPES.TOO_LARGE],
      warnings: [],
    };
  }

  const buffer = await file.arrayBuffer();

  let jszip: JSZip;
  try {
    jszip = await JSZip.loadAsync(buffer);
  } catch (e) {
    return {
      success: false,
      classification: emptyClassification(),
      inventory: [],
      extraction: null,
      summary: null,
      errors: [ERROR_TYPES.INVALID_ZIP, (e as Error)?.message ?? "unknown error"],
      warnings: [],
    };
  }

  const inventory: string[] = [];
  jszip.forEach((relPath: string, f: JSZip.JSZipObject) => {
    if (!f.dir) inventory.push(relPath);
  });

  if (inventory.length === 0) {
    return {
      success: false,
      classification: emptyClassification(),
      inventory: [],
      extraction: null,
      summary: null,
      errors: [ERROR_TYPES.EMPTY_ZIP],
      warnings: [],
    };
  }

  const { classification } = await classifyZip(jszip);

  if (!classification.supported) {
    const errorList: string[] = [ERROR_TYPES.UNSUPPORTED_FORMAT];
    if (classification.unsupportedReason) {
      errorList.push(classification.unsupportedReason);
    }
    return {
      success: false,
      classification,
      inventory,
      extraction: null,
      summary: null,
      errors: errorList,
      warnings: [],
    };
  }

  let parserResult: ParserResult;
  try {
    parserResult = await withTimeout(dispatchVendor(classification.vendor, jszip, buffer), PROCESSING_TIMEOUT_MS);
  } catch (e) {
    const message = (e as Error)?.message ?? "unknown error";
    return {
      success: false,
      classification,
      inventory,
      extraction: null,
      summary: null,
      errors: [message === ERROR_TYPES.TIMEOUT ? ERROR_TYPES.TIMEOUT : ERROR_TYPES.PARSING_ERROR, message],
      warnings: [],
    };
  }

  const enrichment = enrichCrops(parserResult.crops, classification.vendor);
  warnings.push(...parserResult.warnings, ...enrichment.warnings);

  if (enrichment.crops.length === 0) {
    errors.push(ERROR_TYPES.NO_CROPS_FOUND);
  }

  // Parser-derived year wins over filename inference: it comes from the
  // explicit antragsjahr / wirtschaftsjahr / DBF year column, which is
  // strictly more authoritative than path heuristics.
  const finalClassification: FormatClassification = parserResult.year ? { ...classification, year: parserResult.year } : classification;

  const summary = buildSummary(enrichment.crops);
  const processingTime = Date.now() - start;

  return {
    success: enrichment.crops.length > 0,
    classification: finalClassification,
    inventory,
    extraction: {
      method: parserResult.method,
      crops: enrichment.crops,
      cropCount: enrichment.crops.length,
      totalArea: summary.totalArea,
      processingTime,
    },
    summary,
    errors,
    warnings: dedupeWarnings(warnings),
  };
};
